import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Church, LayoutDashboard, LogOut, MapPinned, Search, UserRound, UsersRound } from 'lucide-react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'

const navigation = [
  { to: '/overview', label: 'Overview', icon: LayoutDashboard },
  { to: '/search', label: 'Search records', icon: Search },
  { to: '/plots', label: 'Plots', icon: MapPinned },
  { to: '/people', label: 'People', icon: UsersRound },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', keydown)
    return () => window.removeEventListener('keydown', keydown)
  }, [])

  useEffect(() => {
    if (!location.pathname.startsWith('/search')) setQuery('')
  }, [location.pathname])

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    const term = query.trim()
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : '/search')
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark" aria-hidden="true"><Church size={21} strokeWidth={1.8} /></div>
          <div><strong>St. Vincent de Paul</strong><span>Cemetery Manager</span></div>
        </div>
        <nav className="primary-nav" aria-label="Primary">
          {navigation.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}>
              <Icon size={18} aria-hidden="true" /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="account-row"><div className="avatar"><UserRound size={15} /></div><div><strong>{session?.user.email}</strong><span>Authenticated staff</span></div></div>
          <button className="sidebar-action" type="button" onClick={() => void signOut()}><LogOut size={16} /> Sign out</button>
        </div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <form className="global-search" role="search" onSubmit={submitSearch}>
            <Search size={18} aria-hidden="true" />
            <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, section, row, or grave…" aria-label="Search cemetery records" />
            <kbd>⌘ K</kbd>
          </form>
        </header>
        <main className="page-content">{children}</main>
      </div>
    </div>
  )
}
